import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import api from '@/lib/api';
import {
  clearSessionMemory, decodeTokenTimes, isSafeReturnPath, measureClockOffset, toDeviceTimes, writeSessionNotice,
} from '@/components/entry/session';
import { createPasskey, getPasskey, markPasskeyOffer } from '@/components/entry/passkeys';
import { REMEMBER_KEY, refreshRememberedSession } from '@/lib/sessionRefresh';

const AuthContext = createContext(null);
const LAST_USERNAME_KEY = 'last_username';
// Device clock − server clock (ms), measured at sign-in and kept with the token (see session.js).
const CLOCK_OFFSET_KEY = 'token_clock_offset';
// "Keep me signed in on this device" (01 #6): until when this device is remembered (ISO, from the server) is
// stored under REMEMBER_KEY (lib/sessionRefresh). The secret itself is an httpOnly cookie the page cannot read; this
// only says a refresh is worth trying.
// Sign-in names of these roles are never remembered on the device: a shared site tablet must not
// show the next person an admin account name (the reason 'admin' left the field's placeholder).
const UNREMEMBERED_ROLES = new Set(['Super Admin', 'Admin']);
// setTimeout cannot wait longer than ~24.8 days; longer waits are re-armed.
const MAX_TIMER_MS = 2_000_000_000;
// A remembered session renews its access token this long before it runs out (10 % of its life, 1–5 min),
// and retries every 30 s while the server cannot be reached.
const MAX_REFRESH_LEAD_MS = 5 * 60_000;
const MIN_REFRESH_LEAD_MS = 60_000;
const REFRESH_RETRY_MS = 30_000;

// Sign-in uses its own client: the shared `api` instance treats every 401 as "session expired" and
// hard-redirects to /login, which reloaded the sign-in page on a wrong password and wiped the error
// message and the typed username. Here a 401 / 429 simply rejects so the page can explain it.
// `withCredentials`: the remembered-sign-in cookie (httpOnly, Path=/api/auth) travels with these calls only.
const authClient = axios.create({
  baseURL: api.defaults.baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null || value === undefined || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage blocked / full — the session still works for this tab
  }
};

const clearStoredAuth = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(CLOCK_OFFSET_KEY);
  } catch {
    // storage blocked — nothing stored then either
  }
};

/** Until when this device is remembered (epoch ms), or 0. */
export const rememberedUntil = () => {
  const value = Date.parse(readStorage(REMEMBER_KEY) || '');
  return Number.isFinite(value) && value > Date.now() ? value : 0;
};

const forgetDevice = () => writeStorage(REMEMBER_KEY, null);

/*
  Continue with Microsoft 365 (01 #8): the `state` of the sign-in THIS tab started (sessionStorage: this tab only,
  kept across the trip to Microsoft and back). A return to /login?code=…&state=… that this tab did not start — a
  link someone sent — is refused before anything goes to the server. The server checks the same thing on its own
  side (httpOnly binding cookie, backend/auth_microsoft.py); this is the second, earlier check.
*/
const SSO_STATE_KEY = 'sso_pending_state';
export const SSO_NOT_STARTED_HERE = 'This Microsoft 365 sign-in was not started in this browser. Start it again here.';

/** sessionStorage, or null where the browser blocks it (the server-side check still applies then). */
const tabStorage = () => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const stateOf = (authorizeUrl) => {
  try {
    return new URL(authorizeUrl).searchParams.get('state') || '';
  } catch {
    return '';
  }
};

/** Forget the Microsoft 365 sign-in this tab started (it came back cancelled, refused or used). */
export function forgetMicrosoftSignIn() {
  try {
    tabStorage()?.removeItem(SSO_STATE_KEY);
  } catch {
    // storage blocked — nothing was kept then either
  }
}

/** The page the browser is on, as a return target (empty on /login). */
const currentPath = () => {
  if (typeof window === 'undefined') return '';
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return isSafeReturnPath(path) ? path : '';
};

/**
 * The username of the last successful sign-in on this device ('' when none). It deliberately survives
 * Log out (so the sign-in page can focus Password); the page offers "Not you?" to forget it.
 */
export const getRememberedUsername = () => readStorage(LAST_USERNAME_KEY) || '';

export const forgetRememberedUsername = () => {
  try {
    localStorage.removeItem(LAST_USERNAME_KEY);
  } catch {
    // storage blocked — nothing was remembered then either
  }
};

/** Session times of a stored token on this device's clock (null: no client-side expiry). */
const storedSessionTimes = (token) => {
  const raw = Number(readStorage(CLOCK_OFFSET_KEY));
  // A token stored before the offset was kept (or by a test): assume the clocks agree.
  return toDeviceTimes(decodeTokenTimes(token), Number.isFinite(raw) ? raw : 0);
};

/** Who a token belongs to (its `sub`), without trusting it for anything else. */
const tokenSubject = (token) => {
  try {
    const part = String(token || '').split('.')[1] || '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (part.length % 4)) % 4));
    return String(JSON.parse(json)?.sub || '');
  } catch {
    return '';
  }
};

/** How long before the end a remembered session renews (ms). */
export const refreshLeadMs = (times) => {
  const life = times?.exp && times?.iat ? times.exp - times.iat : 8 * 3_600_000;
  return Math.max(MIN_REFRESH_LEAD_MS, Math.min(MAX_REFRESH_LEAD_MS, Math.round(life * 0.1)));
};

/** Public: which ways to sign in this server offers. Never throws (everything off when unreachable). */
export async function fetchSignInOptions() {
  const fallback = { remember_me: { enabled: true, days: 14 }, microsoft_sso: { enabled: false }, passkeys: { enabled: false }, loaded: false };
  try {
    const res = await authClient.get('/auth/sign-in-options');
    const data = res.data || {};
    return {
      remember_me: {
        enabled: data.remember_me?.enabled !== false,
        days: Number(data.remember_me?.days) > 0 ? Number(data.remember_me.days) : 14,
      },
      microsoft_sso: { enabled: Boolean(data.microsoft_sso?.enabled) },
      passkeys: { enabled: Boolean(data.passkeys?.enabled) },
      loaded: true,
    };
  } catch {
    return fallback;
  }
}

/*
  Session length: the token carries `exp` (8 h by default, backend JWT_ACCESS_TOKEN_MINUTES). The provider
  sets `sessionExpired` the moment it runs out (timer + a check whenever the tab becomes visible again,
  since timers sleep with the laptop). `sessionTimes` ({ exp, iat }) are on this device's clock: the
  server's times shifted by the clock offset measured at sign-in, so a device whose clock is hours off
  still gets its full session. It does not sign out by itself: the shell (Layout) decides how —
  straight to /login?next=…&reason=expired, or first letting the user copy unsaved edits — and then calls
  `endSession({ path, label })`, which leaves a notice for the sign-in page.

  Keep me signed in (01 #6): a remembered device renews the token a few minutes before it runs out
  (POST /auth/refresh with the httpOnly cookie), so the session never ends while the device is remembered
  (14 days by default). Opening the app with a token that ran out (laptop asleep overnight), or landing on
  /login after the server refused a token, first tries the same refresh. Tabs share the newest token
  (localStorage + the `storage` event), so only one of them needs to renew it.
*/
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionTimes, setSessionTimes] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [rememberUntil, setRememberUntil] = useState(() => rememberedUntil());
  // Set when a remembered device restored a session that had ended (the sign-in page then goes on by itself).
  const [resumed, setResumed] = useState(false);
  const refreshing = useRef(null);
  const userRef = useRef(null);
  userRef.current = user;

  /** Store a fresh session from any sign-in answer ({ token, user, remember_until }). */
  const applySession = useCallback((data, { remember } = {}) => {
    const serverTimes = decodeTokenTimes(data.token);
    // The token was issued just now, on the server's clock: remember how far this device's clock is off.
    const offset = measureClockOffset(serverTimes);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    writeStorage(CLOCK_OFFSET_KEY, String(Math.round(offset)));
    if (remember !== undefined) {
      // A new sign-in: remembered only when asked for and granted by the server.
      writeStorage(REMEMBER_KEY, data.remember_until || null);
    } else if (data.remember_until) {
      writeStorage(REMEMBER_KEY, data.remember_until);
    }
    setRememberUntil(rememberedUntil());
    // A token issued seconds ago never ends the session on this device: its deadline is now + its lifetime.
    setSessionTimes(toDeviceTimes(serverTimes, offset));
    setSessionExpired(false);
    setUser(data.user);
  }, []);

  /**
   * Renew a remembered session. 'ok' | 'refused' (the server forgot this device: signed out elsewhere, password
   * changed, period over) | 'offline' (try again later). One request at a time per tab.
   */
  const refreshSession = useCallback(async () => {
    if (refreshing.current) return refreshing.current;
    const run = (async () => {
      try {
        // Shared with lib/api.js (a 401 on a remembered device): one refresh at a time in this tab.
        applySession(await refreshRememberedSession(authClient));
        return 'ok';
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          forgetDevice();
          setRememberUntil(0);
          return 'refused';
        }
        return 'offline';
      } finally {
        refreshing.current = null;
      }
    })();
    refreshing.current = run;
    return run;
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;
    const stored = readStorage('user');
    const token = readStorage('token');
    const restore = async (times) => {
      const outcome = await refreshSession();
      if (cancelled) return;
      if (outcome !== 'ok' && times) {
        // Not renewed: say so on the sign-in page and come back here after.
        writeSessionNotice({ reason: 'expired', path: currentPath(), times });
        clearStoredAuth();
      }
      if (outcome === 'ok') setResumed(true);
      setLoading(false);
    };
    if (stored && token) {
      const times = storedSessionTimes(token);
      if (times && Date.now() >= times.exp) {
        if (rememberedUntil()) {
          restore(times);
          return () => { cancelled = true; };
        }
        // Opened with a token that already ran out: say so on the sign-in page and come back here after,
        // instead of letting the first request bounce through a full reload.
        writeSessionNotice({ reason: 'expired', path: currentPath(), times });
        clearStoredAuth();
      } else {
        try {
          setUser(JSON.parse(stored));
          setSessionTimes(times);
        } catch {
          clearStoredAuth();
        }
      }
    } else if (rememberedUntil()) {
      // No token (the server refused one and lib/api.js sent the browser to /login): a remembered device
      // signs straight back in.
      restore(null);
      return () => { cancelled = true; };
    }
    setLoading(false);
    return () => { cancelled = true; };
    // Runs once on start; refreshSession is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !sessionTimes?.exp || sessionExpired) return undefined;
    let timer = null;
    let cancelled = false;
    const schedule = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(check, Math.max(0, Math.min(ms, MAX_TIMER_MS)));
    };
    async function check() {
      if (cancelled) return;
      const left = sessionTimes.exp - Date.now();
      if (rememberedUntil()) {
        const lead = refreshLeadMs(sessionTimes);
        if (left <= lead) {
          // Another tab may have renewed it already: use its token instead of asking again.
          const shared = readStorage('token');
          const sharedTimes = shared && tokenSubject(shared) === String(userRef.current?.id || '') ? storedSessionTimes(shared) : null;
          if (sharedTimes?.exp && sharedTimes.exp - Date.now() > lead) {
            setSessionTimes(sharedTimes);
            return;
          }
          const outcome = await refreshSession();
          if (cancelled || outcome === 'ok') return;
          if (outcome === 'offline') {
            schedule(REFRESH_RETRY_MS);
            return;
          }
          // refused: the session ends as it would have without "Keep me signed in".
        } else {
          schedule(left - lead + 250);
          return;
        }
      }
      const remaining = sessionTimes.exp - Date.now();
      if (remaining <= 0) {
        setSessionExpired(true);
        return;
      }
      schedule(remaining + 250);
    }
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') check();
    };
    check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user, sessionTimes, sessionExpired, refreshSession]);

  // lib/api.js renewed the session after a 401 on a remembered device: take the new token / user over.
  useEffect(() => {
    const onRefreshed = (event) => {
      const data = event?.detail;
      if (!data?.token) return;
      if (userRef.current && tokenSubject(data.token) !== String(userRef.current.id || '')) return;
      applySession(data);
    };
    window.addEventListener('app:session-refreshed', onRefreshed);
    return () => window.removeEventListener('app:session-refreshed', onRefreshed);
  }, [applySession]);

  // Another tab renewed (or replaced) the token: follow its end time, so this tab neither renews again nor ends early.
  useEffect(() => {
    if (!user) return undefined;
    const onStorage = (event) => {
      if (event.key === REMEMBER_KEY) setRememberUntil(rememberedUntil());
      if (event.key !== 'token' || !event.newValue) return;
      if (tokenSubject(event.newValue) !== String(userRef.current?.id || '')) return;
      const times = storedSessionTimes(event.newValue);
      if (times?.exp) {
        setSessionTimes(times);
        setSessionExpired(false);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user]);

  // `identifier` is the Sara username (the backend field is still called `email`).
  const login = useCallback(async (identifier, password, { remember = false } = {}) => {
    const res = await authClient.post('/auth/login', { email: identifier, password, remember: Boolean(remember) });
    if (UNREMEMBERED_ROLES.has(String(res.data.user?.role || '').trim())) {
      forgetRememberedUsername();
    } else {
      try {
        localStorage.setItem(LAST_USERNAME_KEY, String(identifier || '').trim());
      } catch {
        // storage full / blocked — remembering the username is only a convenience
      }
    }
    clearSessionMemory();
    // After a password sign-in the shell may offer "Use Face ID next time" (01 #9) once.
    markPasskeyOffer();
    applySession(res.data, { remember: Boolean(remember) });
    return res.data;
  }, [applySession]);

  /** Continue with Microsoft 365 (01 #8): where to send the browser. Throws the server's 503 while it is off. */
  const startMicrosoftSignIn = useCallback(async ({ next = '', remember = false } = {}) => {
    const res = await authClient.post('/auth/microsoft/start', { next: isSafeReturnPath(next) ? next : '', remember: Boolean(remember) });
    const url = res.data.authorize_url;
    try {
      const state = stateOf(url);
      if (state) tabStorage()?.setItem(SSO_STATE_KEY, state);
      else tabStorage()?.removeItem(SSO_STATE_KEY);
    } catch {
      // storage blocked / full — the server still ties the sign-in to this browser
    }
    return url;
  }, []);

  /**
   * Microsoft sent the browser back with ?code=&state=: finish the sign-in. Returns { next } on success. Refuses
   * (without asking the server) a return this tab did not start.
   */
  const finishMicrosoftSignIn = useCallback(async ({ code, state }) => {
    const storage = tabStorage();
    if (storage) {
      let pending = null;
      try {
        pending = storage.getItem(SSO_STATE_KEY);
      } catch {
        pending = undefined; // unreadable: leave the check to the server
      }
      forgetMicrosoftSignIn(); // single use, whatever happens next
      if (pending !== undefined && (!pending || pending !== state)) throw new Error(SSO_NOT_STARTED_HERE);
    }
    const res = await authClient.post('/auth/microsoft/callback', { code, state });
    clearSessionMemory();
    applySession(res.data, { remember: Boolean(res.data.remember_until) });
    return { next: isSafeReturnPath(res.data.next) ? res.data.next : '' };
  }, [applySession]);

  /** Sign in with a passkey (Face ID / fingerprint) — no username needed (01 #9). */
  const signInWithPasskey = useCallback(async ({ remember = false } = {}) => {
    const start = await authClient.post('/auth/passkeys/login/options');
    const credential = await getPasskey(start.data.options);
    const res = await authClient.post('/auth/passkeys/login/verify', {
      challenge_id: start.data.challenge_id, credential, remember: Boolean(remember),
    });
    clearSessionMemory();
    applySession(res.data, { remember: Boolean(remember) });
    return res.data;
  }, [applySession]);

  /** Add a passkey for the signed-in user on this device. Returns the stored passkey ({ id, name, … }). */
  const registerPasskey = useCallback(async ({ name } = {}) => {
    const start = await api.post('/auth/passkeys/register/options');
    const credential = await createPasskey(start.data.options);
    const res = await api.post('/auth/passkeys/register/verify', { challenge_id: start.data.challenge_id, credential, name });
    return res.data;
  }, []);

  /** Explicit sign out: forget the session, the page this tab was on and (if remembered) this device. */
  const logout = useCallback(() => {
    if (rememberedUntil() || readStorage(REMEMBER_KEY)) {
      // Revokes the remembered sign-in on the server and clears its cookie; nothing to wait for.
      authClient.post('/auth/logout').catch(() => {});
    }
    forgetDevice();
    setRememberUntil(0);
    clearStoredAuth();
    clearSessionMemory();
    setSessionTimes(null);
    setSessionExpired(false);
    setResumed(false);
    setUser(null);
  }, []);

  /**
   * The session ran out: sign out but keep the way back. Pass `{ path, label }` to (re)write the notice for
   * the sign-in page; the shell writes it before it opens /login, and the sign-in page then calls
   * `endSession()` without arguments to finish.
   */
  const endSession = useCallback((notice) => {
    if (notice) writeSessionNotice({ reason: 'expired', path: notice.path || currentPath(), label: notice.label, times: sessionTimes });
    clearStoredAuth();
    setSessionTimes(null);
    setSessionExpired(false);
    setResumed(false);
    setUser(null);
  }, [sessionTimes]);

  const value = useMemo(() => ({
    user,
    login,
    logout,
    loading,
    sessionTimes,
    sessionExpired,
    endSession,
    rememberUntil,
    resumed,
    refreshSession,
    startMicrosoftSignIn,
    finishMicrosoftSignIn,
    signInWithPasskey,
    registerPasskey,
  }), [
    user, login, logout, loading, sessionTimes, sessionExpired, endSession, rememberUntil, resumed, refreshSession,
    startMicrosoftSignIn, finishMicrosoftSignIn, signInWithPasskey, registerPasskey,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
